import {Exclude, Expose, instanceToPlain, plainToClass, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {TimeGroup, toTimeGroup} from "../time-group";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {ObjectCostRateModelArrayValidator, ObjectCostRateModelEntity} from "../object-cost-rate-model";
import {Exportable} from "../exportable";
import {adjustFromTimeZone, format} from "../../date/date";
import {FlowAllocationType, toFlowAllocationType} from "./flow-allocation-type";
import {CompanyType, toCompanyType} from "../company";
import {toTransportCategory, TransportCategory} from "../transport-category";
import {RouteArrayValidator, RouteEntity} from "../route";
import { CostedEntity } from "../costed-entity";

export const PipelineFlowTransportValidator = t.intersection([
  t.type({
    id: t.string,
    pipelineId: t.string,
    sourceSiteId: t.string,
    destinationSiteId: t.string,
    timeGroup: t.string,
    dttm: t.number,
    quantity: t.number,
    grossQuantity: t.number,
    unitOfMeasure: t.string,
    factor: t.number,
    freightTypeId: t.string,
    regionId: t.string,
    allocationType: t.string,
    category: t.string,
  }),
  t.partial({
    standardQuantity: t.number,
    netQuantity: t.number,
    observedTemperature: t.number,
    temperatureUnitOfMeasure: t.string,
    observedPressure: t.number,
    pressureUnitOfMeasure: t.string,
    observedGravity: t.number,
    correctedGravity: t.number,
    bswPercentage: t.number,
    regionGeographicName: t.string,
    reportId: t.string,
    pipelineName: t.string,
    sourceSiteName: t.string,
    sourceSiteCompanyId: t.string,
    sourceSiteCompanyName: t.string,
    sourceSiteCompanyType: t.string,
    sourceWellId: t.string,
    sourceWellName: t.string,
    sourceRoutes: RouteArrayValidator,
    destinationSiteName: t.string,
    destinationSiteCompanyId: t.string,
    destinationSiteCompanyName: t.string,
    destinationSiteCompanyType: t.string,
    destinationWellId: t.string,
    destinationWellName: t.string,
    destinationRoutes: RouteArrayValidator,
    freightTypeName: t.string,
    serviceTypeId: t.string,
    serviceTypeName: t.string,
    objectCostRateModels: ObjectCostRateModelArrayValidator,
    endDttm: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const PipelineFlowTransportArrayValidator = t.array(
  PipelineFlowTransportValidator
);

export type PipelineFlowTransport = t.TypeOf<
  typeof PipelineFlowTransportValidator
>;
export type PipelineFlowTransportArray = t.TypeOf<
  typeof PipelineFlowTransportArrayValidator
>;

@Exclude()
export class PipelineFlowTransportEntity extends CostedEntity implements Exportable {
  @Expose()
  id!: string;

  @Expose()
  pipelineId!: string;

  @Expose()
  pipelineName?: string;

  @Expose()
  sourceSiteId!: string;

  @Expose()
  sourceSiteName?: string;

  @Expose()
  sourceSiteCompanyId?: string;

  @Expose()
  sourceSiteCompanyName?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCompanyType))
  sourceSiteCompanyType?: CompanyType;

  @Expose()
  sourceWellId?: string;

  @Expose()
  sourceWellName?: string;

  @Expose()
  @Type(() => RouteEntity)
  sourceRoutes?: RouteEntity[];

  @Expose()
  destinationSiteId!: string;

  @Expose()
  destinationSiteName?: string;

  @Expose()
  destinationSiteCompanyId?: string;

  @Expose()
  destinationSiteCompanyName?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCompanyType))
  destinationSiteCompanyType?: CompanyType;

  @Expose()
  destinationWellId?: string;

  @Expose()
  destinationWellName?: string;

  @Expose()
  @Type(() => RouteEntity)
  destinationRoutes?: RouteEntity[];

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTimeGroup))
  timeGroup!: TimeGroup;

  @Expose()
  dttm!: number;

  @Expose()
  readonly endDttm?: number;

  @Expose()
  quantity!: number;

  @Expose()
  grossQuantity!: number;

  @Expose()
  unitOfMeasure!: string;

  @Expose()
  standardQuantity?: number

  @Expose()
  netQuantity?: number;

  @Expose()
  observedTemperature?: number;

  @Expose()
  temperatureUnitOfMeasure?: string

  @Expose()
  observedPressure?: number

  @Expose()
  pressureUnitOfMeasure?: string

  @Expose()
  observedGravity?: number;

  @Expose()
  correctedGravity?: number

  @Expose()
  bswPercentage?: number;

  @Expose()
  factor!: number;

  @Expose()
  freightTypeId!: string;

  @Expose()
  freightTypeName?: string;

  @Expose()
  serviceTypeId?: string;

  @Expose()
  serviceTypeName?: string;

  @Expose()
  regionId!: string;

  @Expose()
  regionGeographicName?: string

  @Expose()
  reportId?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toFlowAllocationType))
  allocationType!: FlowAllocationType;

  @Expose()
  @Type(() => ObjectCostRateModelEntity)
  objectCostRateModels?: ObjectCostRateModelEntity[];

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category!: TransportCategory;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'PIPE_FLOW_TRANSPORT';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for PipelineFlowTransportEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
    super();
  }

  static from(
    pipelineFlowTransport: PipelineFlowTransport
  ): PipelineFlowTransportEntity {
    return plainToClass(PipelineFlowTransportEntity, pipelineFlowTransport, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<PipelineFlowTransport> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(PipelineFlowTransportValidator, object);
  }

  asTypeValue(): PipelineFlowTransport {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(PipelineFlowTransportValidator, object);
  }

  get source(): string | undefined {
    if (this.sourceSiteName && this.sourceWellName) {
      return this.sourceSiteName + ' / ' + this.sourceWellName;
    } else if (this.sourceSiteName) {
      return this.sourceSiteName;
    } else if (this.sourceWellName) {
      return this.sourceWellName;
    } else {
      return undefined;
    }
  }

  get destination(): string | undefined {
    if (this.destinationSiteName && this.destinationWellName) {
      return this.destinationSiteName + ' / ' + this.destinationWellName;
    } else if (this.destinationSiteName) {
      return this.destinationSiteName;
    } else if (this.destinationWellName) {
      return this.destinationWellName;
    } else {
      return undefined;
    }
  }

  getObjectCostRateModelById(id: string): ObjectCostRateModelEntity | undefined {
    return this.objectCostRateModels?.find((ocrm) => ocrm.id === id)
  }

  getObjectCostRateModelByName(name: string): ObjectCostRateModelEntity | undefined {
    return this.objectCostRateModels?.find((ocrm) => ocrm.rateModelName === name)
  }

  readonly CSV_HEADERS: string[] = [
    'Pipeline',
    'Source Company',
    'Source Site',
    'Source Well',
    'Destination Company',
    'Destination Site',
    'Destination Well',
    'Date',
    'Time Group',
    'Quantity',
    'Standard Quantity',
    'Net Quantity',
    'Unit of Measure',
    'Observed Temperature',
    'Temperature Unit of Measure',
    'Observed Pressure',
    'Pressure Unit of Measure',
    'Observed Gravity',
    'Corrected Gravity',
    'BSW Percentage',
    'Factor',
    'Allocation',
    'Freight Type',
    'Service Type',
    'Category',
  ];

  asCsv(): string[] {
    return [
      this.pipelineName ?? '',
      this.sourceSiteCompanyName ?? '',
      this.sourceSiteName ?? '',
      this.sourceWellName ?? '',
      this.destinationSiteCompanyName ?? '',
      this.destinationSiteName ?? '',
      this.destinationWellName ?? '',
      format(adjustFromTimeZone(this.dttm), 'yyyy-MM-dd HH:mm:ss'),
      this.timeGroup.code,
      this.grossQuantity.toString(),
      this.standardQuantity?.toString() ?? '',
      this.netQuantity?.toString() ?? '',
      this.unitOfMeasure,
      this.observedTemperature?.toString() ?? '',
      this.temperatureUnitOfMeasure ?? '',
      this.observedPressure?.toString() ?? '',
      this.pressureUnitOfMeasure ?? '',
      this.observedGravity?.toString() ?? '',
      this.correctedGravity?.toString() ?? '',
      this.bswPercentage?.toString() ?? '',
      this.factor.toString(),
      this.allocationType.code,
      this.freightTypeName ?? '',
      this.serviceTypeName ?? '',
      this.category.code
    ];
  }
}

export interface PipelineFlowTransportSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  regionIds?: string[];
  freightTypeIds?: string[];
  serviceTypeIds?: string[];
  pipelineIds?: string[];
  pipelineCompanyIds?: string[];
  companyIds?: string[];
  timeGroup?: string;
  allocationType?: string;
  category?: string;
}

export interface PipelineCostedReportCriteria {
  fromDttm: number;
  toDttm: number;
  regionId: string;
  rateModelId: string;
  operatorId: string;
  disposalCompanyId: string;
  freightTypeIds?: string[];
  serviceTypeIds?: string[];
  pipelineIds?: string[];
  timeGroup?: string;
  allocationType?: string;
  category?: string[]
  reportName?: string;
  progressSessionId?: string;
}

export interface ServicePipelineReportCriteria {
  fromDttm: number;
  toDttm: number;
  regionId: string;
  operatorId: string;
  serviceTypeIds?: string[];
  pipelineIds?: string[];
  timeGroup?: string;
  allocationType?: string;
  category?: string;
  progressSessionId?: string;
}
